import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {SchemaFieldDataType} from 'folder-items/FileConstants';
import {ClientId} from 'notebook/ELNConstants';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ProcedureSchemaFieldTypeTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {
  getModelRequiredLinkFromSchemaInterface,
  getModelRequiredLinkFromTagSchema,
  SchemaInterfaceWithFields,
} from 'procedures/design/utils/equipmentSchemaHelpers';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {filterProcedureValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {ParameterUsage, ParameterUsages, ProcedureEquipmentSetting} from 'procedures/ProcedureDomainTypes';
import {
  DEFAULT_EQUIPMENT_TYPE,
  EquipmentType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  convertMaterialTypeToSchemaFieldDataType,
  convertMaterialTypeToTypedValue,
  convertTypedValueToMaterialType,
  doesTypedValueHaveLinkedSchema,
  doesTypedValueHaveLinkedSchemaInterface,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import {
  BaseParameterConfigTableMetadata,
  getParameterUsageColumn,
  getParameterUsagesForId,
  makeParameterUsageCell,
} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {
  cellText,
  inferPlannedTextOrBioentityLinkTypedValue,
  makeEntityLinkCell,
  makeStringCell,
  makeTextSchemaFieldCell,
  validationErrorsToEvaluatedCellError,
} from 'procedures/tables/procedureTableAdapterUtils';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {
  FieldType,
  isRequiredLinkCustomSchemaInterface,
  isRequiredLinkTagSchema,
  RequiredLink,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';

export interface ProcedureEquipmentTableMetadata extends BaseParameterConfigTableMetadata {}

export interface ProcedureEquipmentTableRowMetadata {
  apiId: string | null;
  procedureEquipmentId: string | null;
  plannedValueId: string | null;
}

export enum EquipmentConfigColumn {
  NAME = 'NAME',
  TYPE = 'TYPE',
  MODELS = 'MODELS',
  DEFAULT_EQUIPMENT = 'DEFAULT_EQUIPMENT',
  DESCRIPTION = 'DESCRIPTION',
  CONFIRMED_IN = 'CONFIRMED_IN',
}

const ALL_POSSIBLE_COLUMNS_ORDERED = [
  EquipmentConfigColumn.NAME,
  EquipmentConfigColumn.TYPE,
  EquipmentConfigColumn.MODELS,
  EquipmentConfigColumn.DEFAULT_EQUIPMENT,
  EquipmentConfigColumn.DESCRIPTION,
  EquipmentConfigColumn.CONFIRMED_IN,
];

export function columnProvenanceId(column: EquipmentConfigColumn): string {
  return `eq_${column}`;
}

const GET_STC_BY_COLUMN: Record<
  EquipmentConfigColumn,
  (metadata: ProcedureEquipmentTableMetadata) => StructuredTableColumn
> = {
  [EquipmentConfigColumn.NAME]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Name',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(EquipmentConfigColumn.NAME),
  }),
  [EquipmentConfigColumn.TYPE]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      name: 'Equipment type',
      isRequired: true,
      defaultValue: {
        text: '',
        typedValue: convertMaterialTypeToTypedValue(DEFAULT_EQUIPMENT_TYPE),
      },
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE, // for TS
    provenanceUuid: columnProvenanceId(EquipmentConfigColumn.TYPE),
    context: ProcedureSchemaFieldTypeContext.EQUIPMENT,
  }),
  [EquipmentConfigColumn.DEFAULT_EQUIPMENT]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Default equipment',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(EquipmentConfigColumn.DEFAULT_EQUIPMENT),
  }),
  [EquipmentConfigColumn.DESCRIPTION]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Description',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(EquipmentConfigColumn.DESCRIPTION),
  }),
  [EquipmentConfigColumn.CONFIRMED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(EquipmentConfigColumn.CONFIRMED_IN),
    }),
  [EquipmentConfigColumn.MODELS]: () => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Model(s)',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(EquipmentConfigColumn.MODELS),
  }),
};

export function getColumnIndex(
  targetColumn: EquipmentConfigColumn,
  stColumns: Array<StructuredTableColumn>
): number {
  return stColumns.findIndex(({provenanceUuid}) => provenanceUuid === columnProvenanceId(targetColumn));
}

export type ProcedureEquipmentTable = BaseStructuredTable<
  ProcedureEquipmentTableMetadata,
  ProcedureEquipmentTableRowMetadata,
  ClientId
>;

export default class ProcedureEquipmentTableAdapter extends GenericStructuredTableAdapter<
  ProcedureEquipmentTableMetadata,
  ProcedureEquipmentTableRowMetadata
> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.PROCEDURE_EQUIPMENT, {allowNoRowsInEntries: true, ...options});
  }

  override getActionFunctions(
    tableMetadata: ProcedureEquipmentTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<ProcedureEquipmentTableMetadata, ProcedureEquipmentTableRowMetadata> {
    return {secondary: [downloadCSVActionFunction]};
  }

  getDisplayName(table: ProcedureEquipmentTable): string {
    return '';
  }

  getRowDisplayName(_: ProcedureEquipmentTable): string {
    return 'Equipment';
  }

  getBlankRowMetadata(tableMetadata: ProcedureEquipmentTableMetadata): ProcedureEquipmentTableRowMetadata {
    return {apiId: null, procedureEquipmentId: null, plannedValueId: null};
  }

  static getAdapter(): ProcedureEquipmentTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCEDURE_EQUIPMENT
    ] as ProcedureEquipmentTableAdapter;
  }

  override allowPasteTable(): boolean {
    return false;
  }

  override allowDeleteTable(_: ProcedureEquipmentTable): {
    canDelete: boolean;
    reason?: string;
  } {
    return {canDelete: false};
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcedureEquipmentTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return ALL_POSSIBLE_COLUMNS_ORDERED.map((c) => GET_STC_BY_COLUMN[c](metadata));
  }

  // We don't lock any columns in a finalized row
  override getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      ...super.getNonFinalizingColumnTypes(),
      TableColumnType.STRING,
      TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      TableColumnType.CUSTOM,
      TableColumnType.BIOENTITY_SELECTOR,
      TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
    ];
  }

  static isEquipmentSchemaSpecified(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureEquipmentTableRowMetadata>
  ): boolean {
    const equipmentTypeCell = row.cells[getColumnIndex(EquipmentConfigColumn.TYPE, columns)];
    return (
      doesTypedValueHaveLinkedSchema(equipmentTypeCell.typedValue) ||
      doesTypedValueHaveLinkedSchemaInterface(equipmentTypeCell.typedValue)
    );
  }

  // Check against all user-entered fields. Will never report "empty" if there is a backing model;
  // this deliberately prevents users from blanking out fields on an existing object.
  static isEquipmentSettingEmpty(e: ProcedureEquipmentSetting): boolean {
    return (
      e.equipmentVersion.id == null &&
      e.equipmentVersion.name.length === 0 &&
      e.equipmentVersion.description.length === 0 &&
      e.equipmentVersion.dataType.type === DEFAULT_EQUIPMENT_TYPE.type &&
      formatTypedValue(e.equipmentVersion.models).length === 0 &&
      (e.values.length === 0 || formatTypedValue(e.values[0].equipment).length === 0)
    );
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: ProcedureEquipmentTable,
    row: StructuredTableRow<ProcedureEquipmentTableRowMetadata>
  ): boolean {
    // Check first if an equipment schema is specified because rowToEquipmentSetting will error if we pass
    // in an empty array of tag schemas and the equipment type has a linked schema
    if (ProcedureEquipmentTableAdapter.isEquipmentSchemaSpecified(structuredTable.columns, row)) {
      return false;
    }
    const setting = ProcedureEquipmentTableAdapter.rowToEquipmentSetting(structuredTable.columns, row, []);
    return ProcedureEquipmentTableAdapter.isEquipmentSettingEmpty(setting);
  }

  static getDelegateColumnByColumnAndEquipmentType(
    this: void,
    dependentColumn: StructuredTableColumn,
    equipmentType: EquipmentType,
    tagSchemas: Array<{id: string; allFields: Array<SchemaField>}>,
    schemaInterfaces: Array<SchemaInterfaceWithFields>
  ): StructuredTableColumn {
    switch (equipmentType.type) {
      case FieldType.TEXT:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: text',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          // The provenance ID must be stable. It's used to track the LSUV for any cell which uses this column
          provenanceUuid: `${dependentColumn.provenanceUuid}_${equipmentType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.TEXT},
        };
      case FieldType.BIOENTITY_LINK: {
        const {requiredLink} = equipmentType;
        const isRequiredLinkWithTagSchema = isRequiredLinkTagSchema(requiredLink);
        const isRequiredLinkWithSchemaInterface = isRequiredLinkCustomSchemaInterface(requiredLink);

        const provenanceUuidSchemaSuffix = isRequiredLinkWithTagSchema
          ? `-${requiredLink.tagSchema.id}`
          : isRequiredLinkWithSchemaInterface
            ? `-${requiredLink.schemaInterface.id}`
            : '';
        let schemaForFiltering: SchemaFieldDataType | null = null;

        if (dependentColumn.provenanceUuid === columnProvenanceId(EquipmentConfigColumn.DEFAULT_EQUIPMENT)) {
          schemaForFiltering = convertMaterialTypeToSchemaFieldDataType(equipmentType, false);
        } else if (dependentColumn.provenanceUuid === columnProvenanceId(EquipmentConfigColumn.MODELS)) {
          // Even though it is invalid, "Any entity" can be copied into the equipment type cell, so
          // we should allow the Model column to be unconstrained
          let modelRequiredLink: RequiredLink | null = null;
          if (isRequiredLinkWithTagSchema) {
            const equipmentTagSchema = tagSchemas.find((ts) => ts.id === requiredLink.tagSchema.id);
            modelRequiredLink = equipmentTagSchema
              ? getModelRequiredLinkFromTagSchema(equipmentTagSchema)
              : null;
          } else if (isRequiredLinkWithSchemaInterface) {
            const equipmentSchemaInterface = schemaInterfaces.find(
              (si) => si.id === requiredLink.schemaInterface.id
            );
            modelRequiredLink = equipmentSchemaInterface
              ? getModelRequiredLinkFromSchemaInterface(equipmentSchemaInterface)
              : null;
          }
          schemaForFiltering = convertMaterialTypeToSchemaFieldDataType(
            {
              type: equipmentType.type,
              requiredLink: modelRequiredLink,
              unit: null,
            },
            true
          );
        }
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.BIOENTITY_SELECTOR,
            name: 'Delegate: bioentity selector',
            isRequired: false,
          }),
          columnType: TableColumnType.BIOENTITY_SELECTOR,
          provenanceUuid: `${dependentColumn.provenanceUuid}_${equipmentType.type}${provenanceUuidSchemaSuffix}`,
          schemaFieldForFiltering: schemaForFiltering,
        };
      }
      default:
        assert(false, `Unknown equipment type`, {equipmentType});
    }
  }

  private static makeEquipmentTypeInputCell(
    equipmentSetting: ProcedureEquipmentSetting,
    error: Optional<EvaluatedCellError> = null
  ): Cell {
    return {
      text: '',
      typedValue: convertMaterialTypeToTypedValue(equipmentSetting.equipmentVersion.dataType),
      error,
    };
  }

  static makeCellFromEquipment(
    equipmentSetting: ProcedureEquipmentSetting,
    column: StructuredTableColumn,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>
  ): Cell {
    switch (column.provenanceUuid) {
      // Name
      case columnProvenanceId(EquipmentConfigColumn.NAME): {
        const nameCellError = validationErrorsToEvaluatedCellError(
          equipmentSetting.equipmentVersion.cid,
          ProcedureValidationObjectType.EQUIPMENT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [
              ProcedureValidationErrorType.NON_UNIQUE_NAME,
              ProcedureValidationErrorType.INVALID_NAME,
            ],
          })
        );
        return makeStringCell(equipmentSetting.equipmentVersion.name, {
          error: nameCellError,
        });
      }
      // Equipment type
      case columnProvenanceId(EquipmentConfigColumn.TYPE): {
        const invalidSchemaErrors = validationErrorsToEvaluatedCellError(
          equipmentSetting.equipmentVersion.cid,
          ProcedureValidationObjectType.EQUIPMENT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [
              ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE,
              ProcedureValidationErrorType.ARCHIVED_SCHEMA,
            ],
          })
        );
        return ProcedureEquipmentTableAdapter.makeEquipmentTypeInputCell(
          equipmentSetting,
          invalidSchemaErrors
        );
      }

      case columnProvenanceId(EquipmentConfigColumn.MODELS): {
        const equipmentModels = equipmentSetting.equipmentVersion.models;
        const equipmentModelsCellError = validationErrorsToEvaluatedCellError(
          equipmentSetting.equipmentVersion.cid,
          ProcedureValidationObjectType.EQUIPMENT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [
              ProcedureValidationErrorType.MISMATCH_MODELS_TYPE,
              ProcedureValidationErrorType.ARCHIVED_MODEL_ENTITY_SCHEMA,
            ],
          })
        );
        return equipmentModels.type === FieldType.BIOENTITY_LINK && equipmentModels.targetFiles?.length > 0
          ? makeEntityLinkCell(
              equipmentModels.targetFiles.map((targetFile) => ({...targetFile, id: targetFile.id ?? ''})),
              {error: equipmentModelsCellError}
            )
          : makeTextSchemaFieldCell(equipmentModels.value, {error: equipmentModelsCellError});
      }
      // Default equipment
      case columnProvenanceId(EquipmentConfigColumn.DEFAULT_EQUIPMENT): {
        const equipmentPlannedValue = equipmentSetting.values.length > 0 ? equipmentSetting.values[0] : null;
        const defaultEquipment = equipmentPlannedValue?.equipment;
        const defaultEquipmentCellError = validationErrorsToEvaluatedCellError(
          equipmentSetting.equipmentVersion.cid,
          ProcedureValidationObjectType.EQUIPMENT,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE],
          })
        );
        return defaultEquipment?.type === FieldType.BIOENTITY_LINK && defaultEquipment.targetFiles?.length > 0
          ? makeEntityLinkCell(
              [
                {
                  id: defaultEquipment?.targetFiles[0].id ?? '',
                  name: defaultEquipment?.targetFiles[0].name,
                  url: defaultEquipment?.targetFiles[0].url,
                },
              ],
              {error: defaultEquipmentCellError}
            )
          : makeTextSchemaFieldCell(defaultEquipment?.value, {error: defaultEquipmentCellError});
      }

      // Description
      case columnProvenanceId(EquipmentConfigColumn.DESCRIPTION):
        return makeStringCell(equipmentSetting.equipmentVersion.description);

      // Confirmed in
      case columnProvenanceId(EquipmentConfigColumn.CONFIRMED_IN):
        return makeParameterUsageCell(parameterUsages);
      default:
        assert(false, 'Unknown table column', {column});
    }
  }

  static equipmentSettingToRow(
    equipmentSetting: ProcedureEquipmentSetting,
    table: ProcedureEquipmentTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>
  ): StructuredTableRow<ProcedureEquipmentTableRowMetadata> {
    const adapter = ProcedureEquipmentTableAdapter.getAdapter();
    const equipmentPlannedValue = equipmentSetting.values.length > 0 ? equipmentSetting.values[0] : null;

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      rowId: equipmentSetting.equipmentVersion.cid ?? uuidv4(),
      metadata: {
        apiId: equipmentSetting.equipmentVersion.id,
        procedureEquipmentId: equipmentSetting.equipmentVersion.procedureEquipmentId,
        plannedValueId: equipmentPlannedValue?.id ?? null,
      },
      cells: table.columns.map((column) =>
        ProcedureEquipmentTableAdapter.makeCellFromEquipment(
          equipmentSetting,
          column,
          validationErrors,
          parameterUsages
        )
      ),
    };
  }

  private static isProcedureSchemaFieldTypeTypedValue(
    typedValue: TypedValue
  ): typedValue is ProcedureSchemaFieldTypeTypedValue {
    return typedValue.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE;
  }

  private static isBioentityLinkFieldType(fieldType: FieldType): fieldType is FieldType.BIOENTITY_LINK {
    return fieldType === FieldType.BIOENTITY_LINK;
  }

  /**
   * Cell parsing must be done with minimal demands on correctness. This allows for unexpected table
   * interactions like paste or the delete key. Here we simply force any invalid value to TEXT type.
   */
  private static getEquipmentTypeFromCell(
    cell: Cell,
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>,
    schemaInterfaces: Array<SchemaInterfaceWithFields>
  ): EquipmentType {
    const typedValue = cell.typedValue;
    if (
      typedValue != null &&
      ProcedureEquipmentTableAdapter.isProcedureSchemaFieldTypeTypedValue(typedValue) &&
      ProcedureEquipmentTableAdapter.isBioentityLinkFieldType(
        typedValue.fieldDefinition.fieldType as FieldType
      )
    ) {
      return (
        (convertTypedValueToMaterialType(typedValue, tagSchemas, schemaInterfaces) as EquipmentType | null) ??
        DEFAULT_EQUIPMENT_TYPE
      );
    }
    return DEFAULT_EQUIPMENT_TYPE;
  }

  static rowToEquipmentSetting(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureEquipmentTableRowMetadata>,
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>,
    schemaInterfaces: Array<SchemaInterfaceWithFields> = []
  ): ProcedureEquipmentSetting {
    assert(
      columns.length === row.cells.length,
      'Expected row to have same number of cells as number of columns'
    );
    const equipmentTypeCell = row.cells[getColumnIndex(EquipmentConfigColumn.TYPE, columns)];
    const equipmentType = ProcedureEquipmentTableAdapter.getEquipmentTypeFromCell(
      equipmentTypeCell,
      tagSchemas,
      schemaInterfaces
    );
    const defaultEquipmentCell = row.cells[getColumnIndex(EquipmentConfigColumn.DEFAULT_EQUIPMENT, columns)];
    return {
      equipmentVersion: {
        cid: row.rowId,
        id: row.metadata.apiId,
        procedureEquipmentId: row.metadata.procedureEquipmentId,
        name: cellText(row.cells[getColumnIndex(EquipmentConfigColumn.NAME, columns)]).trim(),
        description: cellText(row.cells[getColumnIndex(EquipmentConfigColumn.DESCRIPTION, columns)]).trim(),
        dataType: equipmentType,
        models: inferPlannedTextOrBioentityLinkTypedValue(
          equipmentType,
          row.cells[getColumnIndex(EquipmentConfigColumn.MODELS, columns)]
        ),
      },
      values: [
        {
          id: row.metadata.plannedValueId,
          cid: `${row.rowId}-planned-value`,
          equipment: inferPlannedTextOrBioentityLinkTypedValue(equipmentType, defaultEquipmentCell),
        },
      ],
    };
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    equipment: ProcedureEquipmentSetting,
    row: StructuredTableRow<ProcedureEquipmentTableRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    return columns.map((column) => ({
      columnGroup: null,
      columnType: column.columnType,
      columnProvenanceUuid: column.provenanceUuid,
      submittedAt,
      fieldId: null,
      value:
        ProcedureEquipmentTableAdapter.makeCellFromEquipment(equipment, column, [], parameterUsages)
          .typedValue ?? null,
    }));
  }

  override toTable(equipmentTable: ProcedureEquipmentTable): Table {
    const table = super.toTable(equipmentTable);
    return {
      ...table,
      lockedDims: {allowAddRows: true, allowRemoveRows: true, allowEditCols: false},
      allowColumnEdits: false,
    };
  }

  createTableFromEquipment(
    createProps: CreateStructuredTableProps<ProcedureEquipmentTableMetadata>,
    tableCid: string,
    equipmentSettings: Array<ProcedureEquipmentSetting>,
    procedureValidationErrors: Array<ProcedureValidationError>,
    parameterUsagesByParameterVersionId: ParameterUsages
  ): ProcedureEquipmentTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };
    return {
      ...baseTable,
      rows: equipmentSettings.map((equipmentSetting, index) =>
        ProcedureEquipmentTableAdapter.equipmentSettingToRow(
          equipmentSetting,
          baseTable,
          index,
          procedureValidationErrors,
          getParameterUsagesForId(parameterUsagesByParameterVersionId, equipmentSetting.equipmentVersion.id)
        )
      ),
    };
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
